<script lang="ts">
  // Scoreboard 2v2 — RODADAS (vazas decididas do POV do meu time) +
  // TENTOS + PONTOS (team_a_score vs team_b_score, com cor por meu time).
  // Visual chanfrado verde/roxo (Felipe 2026-05-20: layout cyberpunk).
  //
  // Em torneio: um pequeno "broche" 🏆 sai do topo do losango central
  // pra abrir o chaveamento. Antes ficava como pill fixed top-right e
  // colidia com o painel ELES (Felipe 2026-05-28).
  import type { Card as CardType, MatchSnapshot } from '../../stores/match';
  import { userId } from '../../stores/auth';
  import { findMe2v2 } from '../../lib/teams';
  import { currentMatchTournament, bracketModalOpen } from '../../stores/tournaments';

  type Props = {
    snap: MatchSnapshot;
    vira: CardType | null | undefined;
  };
  let { snap, vira }: Props = $props();

  function openBracket() {
    bracketModalOpen.set(true);
  }

  type TrickResult = 'win' | 'lose' | 'tie' | null;

  let trickDots = $derived.by((): TrickResult[] => {
    const seats = findMe2v2(snap, $userId);
    const myTeam = seats.meTeam;
    const winners = snap.trick_winners ?? [];
    const out: TrickResult[] = [null, null, null];
    for (let i = 0; i < winners.length && i < 3; i++) {
      const w = winners[i];
      if (w === 'tie') out[i] = 'tie';
      else if (w === myTeam) out[i] = 'win';
      else if (w === 'A' || w === 'B') out[i] = 'lose';
    }
    return out;
  });

  let myScore = $derived.by(() => {
    const seats = findMe2v2(snap, $userId);
    return seats.meTeam === 'A' ? (snap.team_a_score ?? 0) : (snap.team_b_score ?? 0);
  });
  let oppScore = $derived.by(() => {
    const seats = findMe2v2(snap, $userId);
    return seats.meTeam === 'A' ? (snap.team_b_score ?? 0) : (snap.team_a_score ?? 0);
  });
  let tentos = $derived(snap.round_points || 1);
  // Suppress unused var (vira ainda exportada por compat)
  void vira;
</script>

<div class="scoreboard">
  <!-- Painel NÓS (esquerda) -->
  <div class="team mine">
    <div class="team-label">NÓS</div>
    <div class="team-sub">Equipe Verde</div>
    <div class="team-score">{myScore}</div>
  </div>

  <!-- VS chip central (com broche 🏆 se estiver em torneio) -->
  <div class="vs">
    {#if $currentMatchTournament}
      <button
        type="button"
        class="bracket-badge"
        onclick={openBracket}
        aria-label="Ver chaveamento do torneio"
        title="Ver chaveamento do torneio"
      >🏆</button>
    {/if}
    <span class="vs-text">VS</span>
    <div class="tentos">
      <span class="tentos-lbl">TENTOS</span>
      <span class="tentos-val">{tentos}</span>
    </div>
    <div class="rodadas">
      {#each trickDots as r, i (i)}
        <span class="dot" data-result={r ?? 'pending'}>{i + 1}</span>
      {/each}
    </div>
  </div>

  <!-- Painel ELES (direita) -->
  <div class="team opp">
    <div class="team-label">ELES</div>
    <div class="team-sub">Equipe Roxa</div>
    <div class="team-score">{oppScore}</div>
  </div>
</div>

<style>
  .scoreboard {
    display: flex;
    align-items: stretch;
    gap: 6px;
    width: 100%;
    max-width: 580px;
    font-family: ui-monospace, "SFMono-Regular", "Menlo", monospace;
  }

  /* Painel de cada time: chanfrado (clip-path corte nos cantos),
   * borda neon, gradient interno. Verde pra MEU time, roxo p/ ELES. */
  .team {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 14px;
    position: relative;
    clip-path: polygon(
      14px 0,
      100% 0,
      100% calc(100% - 14px),
      calc(100% - 14px) 100%,
      0 100%,
      0 14px
    );
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.75));
    overflow: hidden;
  }
  /* Borda neon implementada via box-shadow inset (clip-path corta border) */
  .team::before {
    content: '';
    position: absolute;
    inset: 0;
    clip-path: polygon(
      14px 0,
      100% 0,
      100% calc(100% - 14px),
      calc(100% - 14px) 100%,
      0 100%,
      0 14px
    );
    background: linear-gradient(135deg, transparent 0%, transparent 100%);
    box-shadow: inset 0 0 0 2px currentColor;
    opacity: 0.85;
    pointer-events: none;
  }
  .team.mine {
    color: var(--ok);
    background-image:
      linear-gradient(135deg, rgba(108, 255, 189, 0.18), rgba(0, 0, 0, 0.55) 60%);
  }
  .team.opp {
    color: var(--accent, #d850f0);
    background-image:
      linear-gradient(135deg, rgba(216, 80, 240, 0.22), rgba(0, 0, 0, 0.55) 60%);
  }
  .team-label {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 3px;
    line-height: 1;
  }
  .team-sub {
    font-size: 8px;
    letter-spacing: 2px;
    opacity: 0.7;
    margin-top: 2px;
    text-transform: uppercase;
  }
  .team-score {
    font-size: 28px;
    font-weight: 900;
    line-height: 1;
    margin-top: 4px;
    text-shadow: 0 0 12px currentColor;
  }

  /* VS chip central — losango chanfrado */
  .vs {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 4px 10px;
    background: rgba(0, 0, 0, 0.55);
    border: 1px solid rgba(180, 210, 255, 0.25);
    clip-path: polygon(
      10px 0,
      calc(100% - 10px) 0,
      100% 50%,
      calc(100% - 10px) 100%,
      10px 100%,
      0 50%
    );
    min-width: 110px;
    position: relative;
  }

  /* Broche 🏆 que sai do canto SUPERIOR ESQUERDO do losango quando
     estamos em torneio. Antes ficava no centro e tampava o "VS"
     (Felipe 2026-05-28). Mantido tamanho compacto e o pulse dourado. */
  .bracket-badge {
    position: absolute;
    top: -6px;
    left: 2px;
    width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: linear-gradient(135deg, #ffd54f, #f9a825);
    border: 2px solid #e65100;
    color: #5d3a00;
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    box-shadow:
      0 2px 0 #b85a00,
      0 4px 10px rgba(0, 0, 0, 0.4);
    z-index: 3;
    transition: transform 0.12s, box-shadow 0.12s, filter 0.12s;
    animation: badge-pulse 2.4s ease-in-out infinite;
  }
  .bracket-badge:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 0 #b85a00, 0 6px 14px rgba(0, 0, 0, 0.45);
    filter: brightness(1.08);
  }
  .bracket-badge:active {
    transform: translateY(1px);
    box-shadow: 0 1px 0 #b85a00, 0 3px 6px rgba(0, 0, 0, 0.35);
  }
  @keyframes badge-pulse {
    0%, 100% { filter: drop-shadow(0 0 0 rgba(255, 213, 79, 0)); }
    50%      { filter: drop-shadow(0 0 6px rgba(255, 213, 79, 0.7)); }
  }
  .vs-text {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 3px;
    color: rgba(255, 255, 255, 0.7);
  }
  .tentos {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .tentos-lbl {
    font-size: 7px;
    letter-spacing: 1.5px;
    color: var(--text-dim);
    text-transform: uppercase;
  }
  .tentos-val {
    font-size: 16px;
    font-weight: 800;
    color: var(--warn);
    line-height: 1;
    text-shadow: 0 0 8px rgba(255, 210, 74, 0.55);
  }
  .rodadas {
    display: flex;
    gap: 3px;
    margin-top: 2px;
  }
  .dot {
    width: 16px;
    height: 16px;
    display: grid;
    place-items: center;
    border-radius: 3px;
    border: 1px solid rgba(180, 210, 255, 0.25);
    background: rgba(255, 255, 255, 0.04);
    font-size: 9px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.45);
  }
  .dot[data-result='win'] {
    background: rgba(108, 255, 189, 0.25);
    border-color: var(--ok);
    color: var(--ok);
    box-shadow: 0 0 8px rgba(108, 255, 189, 0.55);
  }
  .dot[data-result='lose'] {
    background: rgba(255, 76, 126, 0.25);
    border-color: var(--danger);
    color: var(--danger);
  }
  .dot[data-result='tie'] {
    background: rgba(255, 210, 74, 0.25);
    border-color: var(--warn);
    color: var(--warn);
  }

  @media (max-width: 520px) {
    .team { padding: 6px 10px; }
    .team-score { font-size: 22px; }
    .vs { min-width: 90px; padding: 4px 6px; }
  }
</style>
